import { z } from 'zod';

import {
  ContributorProps,
  FundProps,
  MediaProps,
} from '@sola/schema/lib/schema.ts';

export enum osIndexEnum {
  Published = 'published',
  Private = 'private',
}

export const ImageResponseDto = z.object({
  url: z.string().optional().nullable(),
  alt: z.string().optional().nullable(),
  ratio: z.string().optional().nullable(),
  height: z.number().optional().nullable(),
  width: z.number().optional().nullable(),
  focus: z.object({ x: z.string(), y: z.string() }).optional().nullable(),
});
export type ImageResponseDto = z.infer<typeof ImageResponseDto>;

export const VideoResponseDto = z.object({
  url: z.string().optional().nullable(),
});
export type VideoResponseDto = z.infer<typeof VideoResponseDto>;

export const MediaDto = z
  .object({
    image: ImageResponseDto.optional().nullable(),
    video: VideoResponseDto.optional().nullable(),
    caption: z.string().optional().nullable(),
  })
  .describe('Media information for an entry');
export type MediaDto = z.infer<typeof MediaDto>;

export const ContributorDto = z
  .object({
    name: z.string().optional(),
    position: z.string().optional().nullable(),
    image: ImageResponseDto.optional().nullable(),
  })
  .describe('Contributor information for an entry');
export type ContributorDto = z.infer<typeof ContributorDto>;

// TAXONOMY
export const EntryTaxonomies = z
  .object({
    articleType: z.string().optional().nullable(),
    articleTopics: z.array(z.string()).optional().nullable(),
    articleThemes: z.array(z.string()).optional().nullable(),
    fundCategory: z.array(z.string()).optional().nullable(),
    fundPromotion: z.array(z.string()).optional().nullable(),
    programCountry: z.array(z.string()).optional().nullable(),
    programRegion: z.array(z.string()).optional().nullable(),
    personalizationSegment: z.array(z.string()).optional().nullable(),
    other: z.array(z.string()).optional().nullable(),
    contentOwner: z.array(z.string()).optional().nullable(),
    cmsOrganization: z.array(z.string()).optional().nullable(),
  })
  .describe('Taxonomy information for an article');
export type EntryTaxonomies = z.infer<typeof EntryTaxonomies>;

// PUBLISHED
export const PublishedEntryInput = z
  .object({
    contentfulEntryID: z.string(),
    contentfulType: z.string(),
    adminTitle: z.string(),
    pageTitle: z.string(),
    path: z.string(),
    summary: z.string().optional().nullable(),
    searchKeywords: z.string().optional().nullable(),
    media: MediaDto.optional().nullable(),
    contributors: ContributorDto.array().optional().nullable(),
    template: z.string().optional().nullable(),
    taxonomy: EntryTaxonomies.optional(),
    fund: FundProps.optional().nullable(),
    json: z.string().optional().nullable(),
    html: z.string().optional().nullable(),
    jsonChildren: z.string().optional().nullable(),
    htmlPublished: z.string().optional().nullable(),
    datePublished: z.string().datetime().optional().nullable(),
    dateModified: z.string().datetime().optional().nullable(),
    dateIndexed: z.string().datetime().optional().nullable(),
    legacyRedirect: z.string().optional().nullable(),
  })
  .describe('Required fields when indexing a published entry');
export type PublishedEntryInput = z.infer<typeof PublishedEntryInput>;

export const PublishedEntryResponseDto = z
  .object({
    contentfulEntryID: z.string().optional(),
    contentfulType: z.string().optional(),
    adminTitle: z.string().optional().nullable(),
    pageTitle: z.string().optional(),
    path: z.string().optional(),
    summary: z.string().optional().nullable(),
    searchKeywords: z.string().optional().nullable(),
    media: MediaDto.optional().nullable(),
    contributors: ContributorDto.array().optional().nullable(),
    template: z.string().optional().nullable(),
    taxonomy: EntryTaxonomies.optional(),
    fund: FundProps.optional().nullable(),
    json: z.string().optional(),
    html: z.string().optional(),
    jsonChildren: z.string().optional(),
    htmlPublished: z.string().optional(),
    datePublished: z.string().datetime().optional(),
    dateModified: z.string().datetime().optional(),
    dateIndexed: z.string().datetime().optional(),
  })
  .describe('A published entry with optional information');
export type PublishedEntryResponseDto = z.infer<
  typeof PublishedEntryResponseDto
>;

// EDITOR
const PrivatePageDocument = z.object({
  adminTitle: z.string().optional().nullable(),
  pageTitle: z.string().optional().nullable(),
  path: z.string().optional().nullable(),
  summary: z.string().optional().nullable(),
  searchKeywords: z.string().optional().nullable(),
  media: MediaDto.optional().nullable(),
  template: z.string().optional().nullable(),
  legacyRedirect: z.string().optional().nullable(),
  taxonomy: EntryTaxonomies.optional().nullable(),
  fund: FundProps.optional().nullable(),
  json: z.string().optional().nullable(),
  parentPageId: z.string().optional().nullable(),
  hasChildren: z.boolean().optional().nullable(),
});
const PrivateContentDocument = z.object({
  adminTitle: z.string().optional().nullable(),
  title: z.string().optional().nullable(),
  subtitle: z.string().optional().nullable(),
  json: z.string().optional().nullable(),
});

export const PrivateEntryInput = z
  .object({
    contentfulEntryID: z.string(),
    contentfulType: z.string(),
    status: z.string(),
    externalDomains: z.string().array().optional(),
    isReferenced: z.boolean(),
    isOrphaned: z.boolean().optional(),
    dateModified: z.string().datetime(),
    dateIndexed: z.string().datetime(),
  })
  .merge(PrivatePageDocument)
  .merge(PrivateContentDocument)
  .describe('Required fields when indexing an private entry');
export type PrivateEntryInput = z.infer<typeof PrivateEntryInput>;

export const PrivateEntryResponseDto = z
  .object({
    contentfulEntryID: z.string(),
    contentfulType: z.string(),
    status: z.string().optional().nullable(),
    externalDomains: z.string().array().optional().nullable(),
    isReferenced: z.boolean().optional().nullable(),
    isOrphaned: z.boolean().optional().nullable(),
    dateModified: z.string().datetime().optional().nullable(),
    dateIndexed: z.string().datetime().optional().nullable(),
  })
  .merge(PrivatePageDocument)
  .merge(PrivateContentDocument)
  .describe('A private entry with optional information');
export type PrivateEntryResponseDto = z.infer<typeof PrivateEntryResponseDto>;

// published
export const searchableFields = [
  'pageTitle.keyword^4',
  'pageTitle.synonyms^2',
  'summary',
  'searchKeywords',
] as const;
export const sortableFields = [
  'pageTitle.keyword',
  'fund.catalogAmount',
  'datePublished',
  'dateModified',
  'dateIndexed',
] as const;
export const facetedFields = [
  'contentfulType.keyword',
  'template.keyword',
  'fund.catalogAmount',
  'taxonomy.articleType.keyword',
  'taxonomy.articleThemes.keyword',
  'taxonomy.articleTopics.keyword',
  'taxonomy.fundCategory.keyword',
  'taxonomy.fundPromotion.keyword',
  'taxonomy.programRegion.keyword',
  'taxonomy.programCountry.keyword',
] as const;

// private
export const searchableFieldsPrivate = [
  'adminTitle^3',
  'pageTitle.keyword^2',
  'summary',
  'searchKeywords',
] as const;
export const sortableFieldsPrivate = [
  'pageTitle.keyword',
  'path.keyword',
  'status.keyword',
  'datePublished',
  'dateModified',
  'dateIndexed',
] as const;
export const facetedFieldsPrivate = [
  'contentfulType.keyword',
  'status.keyword',
  'template.keyword',
  'taxonomy.articleType.keyword',
  'taxonomy.articleThemes.keyword',
  'taxonomy.articleTopics.keyword',
  'taxonomy.fundCategory.keyword',
  'taxonomy.fundPromotion.keyword',
  'taxonomy.programRegion.keyword',
  'taxonomy.programCountry.keyword',
  'externalDomains.keyword',
] as const;

export const facetedFieldNames = {
  'contentfulType.keyword': 'Content Type',
  'template.keyword': 'Template',
  'taxonomy.articleType.keyword': 'Article Type',
  'taxonomy.articleThemes.keyword': 'Article Theme',
  'taxonomy.articleTopics.keyword': 'Article Topic',
  'taxonomy.fundCategory.keyword': 'Fund Category',
  'taxonomy.fundPromotion.keyword': 'Fund Promotion',
  'taxonomy.programRegion.keyword': 'Program Region',
  'taxonomy.programCountry.keyword': 'Program Country',
  'externalDomains.keyword': 'External Domains',
} as const;

// INPUTS
export const IndexSettingsInput = z.object({
  index: z.enum(osIndexEnum),
});
export type IndexSettingsInput = z.infer<typeof IndexSettingsInput>;

export const IndexInput = z.object({
  contentfulEntryID: z.string(),
  contentfulType: z.string(),
  isManual: z.boolean().optional(),
});
export type IndexInput = z.infer<typeof IndexInput>;

export const PublishedSearchResultsInput = z.object({
  query: z.string().optional(),
  offset: z.coerce.number().optional(),
  sort: z.enum(sortableFields).optional(),
  sortDir: z.enum(['asc', 'desc']).optional(),
  filters: z.string().optional(),
  must: z.string().optional(),
  should: z.string().optional(),
  shouldStrictness: z.number().min(0).max(5).optional(),
  terms: z.string().optional(),
  must_not: z.string().optional(),
  ranges: z.string().optional(),
  perPage: z.coerce.number().optional().default(25), // TODO: Zod won't honor optional() when a default is set
  aggs: z.enum(facetedFields).array().optional(),
});
export type PublishedSearchResultsInput = z.infer<
  typeof PublishedSearchResultsInput
>;

export const PrivateSearchResultsInput = z.object({
  query: z.string().optional(),
  offset: z.coerce.number().optional(),
  sort: z.enum(sortableFieldsPrivate).optional(),
  sortDir: z.enum(['asc', 'desc']).optional(),
  filters: z.string().optional(),
  must: z.string().optional(),
  should: z.string().optional(),
  shouldStrictness: z.number().min(0).max(5).optional(),
  terms: z.string().optional(),
  must_not: z.string().optional(),
  ranges: z.string().optional(),
  perPage: z.coerce.number().optional().default(25), // TODO: Zod won't honor optional() when a default is set
  aggs: z.enum(facetedFieldsPrivate).array().optional(),
});
export type PrivateSearchResultsInput = z.infer<
  typeof PrivateSearchResultsInput
>;

export const SearchResultsInput = z.custom<
  PublishedSearchResultsInput | PrivateSearchResultsInput
>();
export type SearchResultsInput = z.infer<typeof SearchResultsInput>;

// RESPONSE
export const SearchResultFacets = z
  .object({
    field: z.enum(facetedFields),
    values: z.object({ key: z.string(), count: z.number() }).array(),
  })
  .optional()
  .array();
export type SearchResultFacets = z.infer<typeof SearchResultFacets>;

export const SearchResultFacetsPrivate = z
  .object({
    // todo: can we make this use a plain string to be more flexible?
    field: z.enum(facetedFieldsPrivate),
    values: z.object({ key: z.string(), count: z.number() }).array(),
  })
  .optional()
  .array();
export type SearchResultFacetsPrivate = z.infer<
  typeof SearchResultFacetsPrivate
>;

export const QueryResponsePublished = z.object({
  items: PublishedEntryResponseDto.array(),
  totalCount: z.number().optional(),
  facets: SearchResultFacets,
});
export type QueryResponsePublished = z.infer<typeof QueryResponsePublished>;

export const QueryResponsePrivate = z.object({
  items: PrivateEntryResponseDto.array(),
  totalCount: z.number().optional(),
  facets: SearchResultFacetsPrivate,
});
export type QueryResponsePrivate = z.infer<typeof QueryResponsePrivate>;

export const MutationResponseStandard = z.object({
  success: z.boolean(),
  error: z.string().nullable().optional(),
});
export type MutationResponseStandard = z.infer<typeof MutationResponseStandard>;

// COMMON
export const SearchResultProps = z.object({
  type: z.string().optional(),
  title: z.string().optional(),
  summary: z.string().optional(),
  searchKeywords: z.string().optional(),
  path: z.string().optional(),
  publishDate: z.string().optional(),
  media: MediaProps.optional(),
  fund: FundProps.optional().nullable(),
  contributors: ContributorProps.array().optional(),
  taxonomyArticleType: z.string().optional(),
  taxonomyArticleTheme: z.string().array().optional(),
  taxonomyTopic: z.string().array().optional(),
  taxonomyFundPromotion: z.string().array().optional(),
  taxonomyFundCategory: z.string().array().optional(),
  taxonomyProgramCountry: z.string().array().optional(),
  taxonomyProgramRegion: z.string().array().optional(),
  taxonomyCmsOrganization: z.string().array().optional(),
});
export type SearchResultProps = z.infer<typeof SearchResultProps>;
